/** @jsx jsx */

import { Fragment } from 'react';

import { Button } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import {
  ACTION_TYPES,
  BASE_DOC_URL_VB,
  NEW_SERVICE_FIELD,
} from 'app/developer-v3/constants';
import {
  useAvroEvent,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';
import { getActionUrl, getAppVersionUrl } from 'app/developer-v3/utils';

import * as CTAs from './CTAs';
import { getLockedHints, getReadOnlyReasons } from './utils';

import type { ZapTaskDetails } from '../types';

type Props = {
  appId: ID;
  hasActions: boolean;
  hasAuthentication: boolean;
  ordinal: number;
  userCount: number;
  version: string;
  zapTaskDetails: ZapTaskDetails['actions'];
};

function CTAActions({
  appId,
  hasActions,
  hasAuthentication,
  ordinal,
  userCount,
  version,
  zapTaskDetails,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const {
    isReadOnly,
    adminOnlyHint,
    lifecyclePendingHint,
    productionOrUserCountLimitExceededHint,
  } = getReadOnlyReasons(currentVersionReadOnlyReason, userCount);
  const lockedHints = getLockedHints(userCount);

  const hint = isReadOnly
    ? adminOnlyHint ||
      lifecyclePendingHint ||
      productionOrUserCountLimitExceededHint
    : lockedHints.needAuth;

  const appVersionUrl = getAppVersionUrl(appId, version);
  const description =
    'Help users write data back to your integration. Actions create or update records through your API.';
  const learnMoreUrl = `${BASE_DOC_URL_VB}/build/action`;
  const actionUrl = getActionUrl({
    actionKey: NEW_SERVICE_FIELD,
    actionType: ACTION_TYPES.creates,
    id: appId,
    version,
  });

  return !isReadOnly && hasAuthentication && hasActions ? (
    <CTAs.Dropdown
      action={
        <Fragment>
          <Button
            aria-label="Add Actions"
            href={actionUrl}
            onClick={() => {
              emitPartnerUserInteractionEvent({
                eventAction: 'click',
                interactionGoal: 'Add an action',
                interactionName: 'integration_home_button_clicked',
                interactionRawText: 'Add Action',
                optionSelected: 'action',
              });
            }}
          >
            Add Action
          </Button>
          <Button
            aria-label="Manage Actions"
            href={`${appVersionUrl}/actions`}
            onClick={() => {
              emitPartnerUserInteractionEvent({
                eventAction: 'click',
                interactionGoal: 'Manage actions',
                interactionName: 'integration_home_button_clicked',
                interactionRawText: 'Manage Actions',
                optionSelected: 'action',
              });
            }}
          >
            Manage Actions
          </Button>
        </Fragment>
      }
      description={description}
      icon="action"
      isActive={false}
      ordinal={ordinal}
      title="Actions"
      type="actions"
      zapTaskDetails={zapTaskDetails}
    />
  ) : (
    <CTAs.Lockable
      action={
        <Button
          aria-label="Create an Action"
          href={actionUrl}
          onClick={() => {
            emitPartnerUserInteractionEvent({
              eventAction: 'click',
              interactionGoal: 'Start creating an action',
              interactionName: 'integration_home_button_clicked',
              interactionRawText: 'Create an Action',
              optionSelected: 'action',
            });
          }}
        >
          Create an Action
        </Button>
      }
      description={description}
      hint={hint}
      icon="action"
      isActive={false}
      isLocked={isReadOnly || !hasAuthentication}
      learnMoreUrl={learnMoreUrl}
      ordinal={ordinal}
      title="Actions"
    />
  );
}

export default CTAActions;
